































































































































































































































































































































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import { appointmentModule } from '@/store/modules/appointment';
import { formatEntityDualPrice } from '@/filters/dual-price';
import { floors } from '@/filters/floors';
import { propertySingular } from '@/filters/property-singular';
import { floorSingular } from '@/filters/floor-singular';
import { searchModule } from '@/store/modules/search';
import { typeProject } from '@/filters/type-project';
import { projectPhase } from '@/filters/project-phase';
import UnitDetailTable from './UnitDetailTable.vue';
import DualPriceDisplay from './DualPriceDisplay.vue';
import { UnitIntegrationDetail } from '@/interfaces/unit-integration';

@Component({
    components: { UnitDetailTable, DualPriceDisplay },
    filters: { floors, propertySingular, floorSingular, typeProject, projectPhase }
})
export default class TableModel extends Vue {
    windowWidth =
        typeof window !== 'undefined' ? window.innerWidth : 1200;

    @Prop({ default: 0 }) readonly type!: number;
    @Prop({ required: true }) readonly models!: any[];
    @Prop({ required: true }) readonly project!: any;
    @Prop({ required: false }) readonly sizeImage!: object;
    @Prop({ type: String }) readonly headerCellClassName!: string;
    @Prop({ type: Number, default: 346 }) readonly maxHeight!: number;
    @Prop({ type: Number }) readonly maxVisibleRows?: number;
    @Prop({ type: String, default: 'text-center f-12 py-0' })
    readonly cellClassName!: string;

    expandedTypologyIds: string[] = [];
    readonly fichaTypologyRowHeightPx = 122;
    readonly fichaMobileCardHeightPx = 240;
    readonly fichaTableHeaderHeightPx = 42;
    readonly unitDetailRowHeightPx = 68;
    readonly unitDetailHeaderHeightPx = 44;
    readonly unitDetailExtraMarginPx = 12;

    mounted() {
        this.syncWindowWidth();
        window.addEventListener('resize', this.syncWindowWidth);
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.syncWindowWidth);
    }

    syncWindowWidth() {
        this.windowWidth = window.innerWidth;
        this.$nextTick(() => this.refreshTableLayout());
    }

    get isMobileViewport() {
        return this.windowWidth < 992;
    }

    get showUnitsDetail() {
        return this.type === 0 || this.type === 1;
    }

    /** Filas visibles con scroll; el valor lo define el componente padre vía prop. */
    get unitsMaxVisibleRows(): number {
        return this.maxVisibleRows ?? 3;
    }

    /** Solo ficha: scroll en tabla de tipologías cuando el padre pasa maxVisibleRows. */
    get isFichaWithModelScroll(): boolean {
        return this.type === 1 && this.maxVisibleRows != null;
    }

    get fichaTableMaxHeight(): number | 'auto' {
        const modelCount = this.models.length;
        const maxRows = this.maxVisibleRows!;

        if (modelCount <= maxRows) {
            return 'auto';
        }

        return (
            this.fichaTableHeaderHeightPx +
            maxRows * this.fichaTypologyRowHeightPx
        );
    }

    get fichaMobileScrollStyle(): Record<string, string> {
        if (!this.isFichaWithModelScroll || !this.isMobileViewport) {
            return {};
        }
        const modelCount = this.models.length;
        const maxRows = this.maxVisibleRows!;

        if (modelCount <= maxRows) {
            return {};
        }

        return {
            maxHeight: `${maxRows * this.fichaMobileCardHeightPx}px`,
            overflowY: 'auto'
        };
    }

    get tableMaxHeight(): string | number {
        if (this.isFichaWithModelScroll) {
            return this.fichaTableMaxHeight;
        }
        if (this.type > 0) {
            return 'auto';
        }
        const expandedCount = this.expandedTypologyIds.length;
        if (!expandedCount) {
            return this.maxHeight;
        }
        const extraHeight =
            expandedCount *
            (this.unitDetailHeaderHeightPx +
                this.unitsMaxVisibleRows * this.unitDetailRowHeightPx +
                this.unitDetailExtraMarginPx);
        return this.maxHeight + extraHeight;
    }

    get visibleColumnCount(): number {
        let count = 2;
        if (this.project.type_project === 1) {
            count += 2;
        }
        if ([1, 2].includes(this.project.type_project) && this.type === 0) {
            count += 1;
        }
        count += 2;
        if (!this.isCustomer) {
            count += 1;
        }
        count += 1;
        if (this.showUnitsDetail) {
            count += 1;
        }
        return count;
    }

    get tableRows(): any[] {
        if (!this.showUnitsDetail) {
            return this.models;
        }
        return this.models.flatMap((row) => {
            const rows = [row];
            if (!this.isMobileViewport && this.isUnitRowExpanded(row)) {
                rows.push({ ...row, _isUnitsDetailRow: true });
            }
            return rows;
        });
    }

    get isCustomer() {
        return !!searchModule.searchSelected;
    }

    hasUnits(row: { unidadesDetalle?: UnitIntegrationDetail[] }) {
        return (row.unidadesDetalle?.length || 0) > 0;
    }

    typologyRowKey(row: { _id?: string }) {
        return String(row._id);
    }

    isUnitRowExpanded(row: { _id?: string; _isUnitsDetailRow?: boolean }) {
        if (row._isUnitsDetailRow) {
            return false;
        }
        return this.expandedTypologyIds.includes(this.typologyRowKey(row));
    }

    toggleUnitsRow(row: { _id?: string; _isUnitsDetailRow?: boolean }) {
        if (row._isUnitsDetailRow) {
            return;
        }
        const key = this.typologyRowKey(row);
        const index = this.expandedTypologyIds.indexOf(key);
        if (index >= 0) {
            this.expandedTypologyIds.splice(index, 1);
        } else {
            this.expandedTypologyIds.push(key);
        }
        this.$nextTick(() => this.refreshTableLayout());
    }

    refreshTableLayout() {
        if (this.isMobileViewport) {
            return;
        }
        const table = this.$refs.projectTable as {
            doLayout?: () => void;
        };
        table?.doLayout?.();
    }

    getTableColumnCount(): number {
        const table = this.$refs.projectTable as {
            store?: { states?: { columns?: unknown[] } };
            columns?: unknown[];
        };
        const columns =
            table?.store?.states?.columns || table?.columns || [];
        return columns.length > 0 ? columns.length : this.visibleColumnCount;
    }

    unitsSpanMethod({
        row,
        columnIndex
    }: {
        row: { _isUnitsDetailRow?: boolean };
        columnIndex: number;
    }) {
        if (row._isUnitsDetailRow) {
            if (columnIndex === 0) {
                return { rowspan: 1, colspan: this.getTableColumnCount() };
            }
            return { rowspan: 0, colspan: 0 };
        }
    }

    resolveRowClassName({
        row
    }: {
        row: { _isUnitsDetailRow?: boolean };
    }): string {
        if (row._isUnitsDetailRow) {
            return 'row-units-detail';
        }
        return 'row-table-project';
    }

    resolveCellClassName({
        row,
        columnIndex
    }: {
        row: { _isUnitsDetailRow?: boolean };
        columnIndex: number;
    }): string {
        if (row._isUnitsDetailRow && columnIndex === 0) {
            return 'units-detail-row-cell';
        }
        return this.cellClassName;
    }

    openModal(row: any) {
        if (this.isCustomer) {
            this.sendWhatsapp(row);
        } else {
            appointmentModule.closeOrOpenDialog(true);
            appointmentModule.setModel({ ...row, project: this.project });
        }
    }

    sendWhatsapp(row: any) {
        const sinOnly = {
            bathrooms : searchModule.searchSelected?.filter?.bathrooms,
            bedrooms : searchModule.searchSelected?.filter?.bedrooms,
            commission : searchModule.searchSelected?.filter?.commission,
            condition : searchModule.searchSelected?.filter?.condition,
            departamento : searchModule.searchSelected?.filter?.departamento,
            discarded : searchModule.searchSelected?.filter?.discarded,
            distrito : searchModule.searchSelected?.filter?.distrito,
            financeBanks : searchModule.searchSelected?.filter?.financeBanks,
            locations : searchModule.searchSelected?.filter?.locations,
            meters : searchModule.searchSelected?.filter?.meters,
            orderBy : searchModule.searchSelected?.filter?.orderBy,
            orientation : searchModule.searchSelected?.filter?.orientation,
            page : searchModule.searchSelected?.filter?.page,
            price : searchModule.searchSelected?.filter?.price,
            projectPhases : searchModule.searchSelected?.filter?.projectPhases,
            projectTypes : searchModule.searchSelected?.filter?.projectTypes,
            provincia : searchModule.searchSelected?.filter?.provincia,
            typologyTypes : searchModule.searchSelected?.filter?.typologyTypes,
            urbanization_id : searchModule.searchSelected?.filter?.urbanization_id,
        }
        const routeData = this.$router.resolve({
            path: `/email-search/project/${this.project.project_id}/${searchModule.searchSelected?._id}`,
            query: sinOnly as any
        });
        const phone = `51${searchModule.searchSelected?.agent?.celular}`;
        const message = encodeURIComponent(
            `Hola agente ${searchModule.searchSelected?.agent?.fullname}, me gustaría agendar una cita para proyecto con las siguientes características:\n * Tipo: ${typeProject(row.type_project)}\n * Metros: ${row.minMetters} m2\n * Precio desde: ${formatEntityDualPrice(row)}\n * Estado de proyecto: ${projectPhase(this.project.project_phase)}\n * Distrito: ${this.project.district}\n * Link del proyecto ${window.location.origin}${routeData.href}`
        );
        const url_whatsap = `https://wa.me/${phone}?text=${message}`;
        const deviceAgent = navigator.userAgent;
        const ios = deviceAgent.toLowerCase().match(/(iphone|ipod|ipad)/);
        if (ios) {
            window.location.href = url_whatsap;
        } else {
            window.open(url_whatsap, '_blank');
        }
    }
}
